/**
 * @module types/settings/dimensionPercentage
 * @description 表格「维度占比」配置（存储字段名 `dimension_percentage`）
 *
 * 与 `column_percentage`（列总计占比）的区别：
 * - `column_percentage`：以「整列（最细维度叉乘组合下该指标）」总和为分母，每行占列合计
 * - `dimension_percentage`：以「所选 group_by 维度的叉乘组合」总和为分母，每行占同维度组合合计
 *   例：选 metric=revenue, group_by=[country] → 对每个 country 求和 revenue，
 *       该行 revenue / 同 country revenue 总和 = 占当日合计占比
 *
 * ## 后端契约（queries.customizeSetting.table.dimension_percentage）— 更新于 2026-06-25
 *
 * 存储结构（**与后端契约完全同构**）：
 * ```json
 * {
 *   "dimension_percentage": {
 *     "metrics": [
 *       { "table_code": "t0", "metric": "revenue", "label": "收入占比(国家)",
 *         "group_by": [{ "table_code": "t0", "column": "country" }] },
 *       { "table_code": "t0", "metric": "revenue", "label": "收入占比(产品)",
 *         "group_by": [{ "table_code": "t0", "column": "product" }] }
 *     ]
 *   }
 * }
 * ```
 *
 * 契约要点：
 * - **不要在 `dimension_percentage` 根级传 `group_by`**；分组维只写在每条 `metrics[]` 配置里
 * - `group_by` 在每条 `metrics[]` 内 ≥1 个维度，且 `column` 须在 `queries.group_by` 中
 * - 同一 `(table_code, metric)` 下可配多条（每条 group_by / label 可不同）
 *   后端按 metrics[] 出现顺序追加 origin_name 后缀：
 *     第 1 条 → `dim_pct_{column}`
 *     第 2 条 → `dim_pct_{column}_2`
 *     第 3 条 → `dim_pct_{column}_3`
 *   `_2` / `_3` 只表示「第 n 条配置」，不表示按哪个维度分组；分组看该条 `group_by`
 * - `metric` 引用 `queries.metrics[].column`（物理列名），`label` 是表头展示名（可重复，trim 后非空，≤100 字）
 * - 校验：metric / group_by.column 不得以 `%` 或 `dim_pct_` 开头；label 允许以 `%` 开头（仅作展示名，不参与列名生成）
 *
 * ## 前端展示与数据绑定
 * - 表头：使用用户配置 `label`
 * - 列绑定 / `column_sort.column`：使用 `dim_pct_{column}`（多条 `_2`/`_3`）
 * - `table_data` / `Sorts.Code`：使用后端返回的系统键（如 `t0_dim_pct_收入`、`t0_dim_pct_收入_2`）
 *
 * ## 列顺序（默认未配 column_sort）
 * 维度列 → 每个源指标：源指标 → 列占比（若有）→ 该源指标下全部维度占比列（按 metrics[] 子序列顺序）→ 对比时间列…
 * 多源指标时，维度占比紧跟各自源指标，不按 metrics[] 全局下标交错：
 *   配置 [revenue+country, orders+country, revenue+product] → 列序
 *   revenue → dim%(country) → dim%(product) → orders → dim%(country)
 */

export interface IDimensionPercentageGroupByItem {
  /** 维度表 code；缺省视为 't0'（与 columnPercentage 对齐） */
  table_code: string;
  /** 维度物理列名（不含 table_code 前缀） */
  column: string;
}

export interface IDimensionPercentageMetricItem {
  /** 指标表 code；缺省视为 't0' */
  table_code: string;
  /** 源指标原始 column，不是展示 label */
  metric: string;
  /** 表头展示名；trim 后非空，最长 100 字；同一指标允许重名；允许任意特殊字符（不受「metric/group_by.column 不得以 % 开头」限制） */
  label: string;
  /**
   * 聚合维度列表（多选）；影响该列的「合计占比」分母
   * - 必填，≥1 个
   * - 顺序影响复合分组
   */
  group_by: IDimensionPercentageGroupByItem[];
}

/**
 * 表格「维度占比」配置（与后端 `dimension_percentage` 同构）
 *
 * 注意：根级不带 `group_by`——`group_by` 写在每条 `metrics[]` 配置里
 */
export interface IDimensionPercentageConfig {
  /** 需要算维度占比的指标列表（每条对应一列输出） */
  metrics: IDimensionPercentageMetricItem[];
}

/**
 * 默认值：空对象，metrics 为冻结空数组
 *
 * 引用稳定：selector 读空态时返回本引用，Object.is 恒等 → 不触发无效重渲染
 * 禁止 mutate；禁止在读取点用 `?? {}` / `?? []` 替代
 */
export const DIMENSION_PERCENTAGE_DEFAULT: IDimensionPercentageConfig =
  Object.freeze({
    metrics: Object.freeze(
      [] as IDimensionPercentageMetricItem[],
    ),
  }) as IDimensionPercentageConfig;

/** label 长度上限（前端契约：最长 100 字；metric / group_by.column 不得以 `%` / `dim_pct_` 开头，但 label 允许以 `%` 开头作为默认形态） */
export const DIMENSION_PERCENTAGE_LABEL_MAX_LENGTH = 100;

/** 后端禁止前缀（metric / group_by.column 不得以这些开头） */
const FORBIDDEN_PREFIXES = ['%', 'dim_pct_'] as const;

/** 判断名称是否触发后端「命名前缀」校验失败 */
export function hasForbiddenPrefix(name: string): boolean {
  if (!name) return false;
  return FORBIDDEN_PREFIXES.some(prefix => name.startsWith(prefix));
}

/** label 长度校验：trim 后 1..MAX */
export function isValidLabel(label: string | undefined): boolean {
  const trimmed = (label || '').trim();
  return trimmed.length >= 1 && trimmed.length <= DIMENSION_PERCENTAGE_LABEL_MAX_LENGTH;
}

/** 单条 group_by 项是否合法（column 非空且非禁用前缀） */
export function isDimensionPercentageGroupByValid(
  item: IDimensionPercentageGroupByItem | undefined,
): boolean {
  if (!item) return false;
  if (!item.column) return false;
  if (hasForbiddenPrefix(item.column)) return false;
  return true;
}

/**
 * 单条 metric 是否合法（与后端契约字段表对齐）
 *
 * 必填：
 * - `metric` 非空且非禁用前缀
 * - `label` trim 后非空、最长 100 字
 * - `group_by` 数组，≥1 个；每项 column 非空且非禁用前缀
 */
export function isDimensionPercentageMetricValid(
  item: IDimensionPercentageMetricItem | undefined,
): boolean {
  if (!item) return false;
  if (!item.metric) return false;
  if (hasForbiddenPrefix(item.metric)) return false;
  if (!isValidLabel(item.label)) return false;
  if (!Array.isArray(item.group_by) || item.group_by.length === 0) {
    return false;
  }
  return item.group_by.every(isDimensionPercentageGroupByValid);
}

/** 整体是否为空（不发送 dimension_percentage 字段） */
export function isEmptyDimensionPercentage(
  cfg: IDimensionPercentageConfig | undefined,
): boolean {
  if (!cfg) return true;
  const m = Array.isArray(cfg.metrics) ? cfg.metrics.length : 0;
  return m === 0;
}

/**
 * 缺省 label：`%${metricDisplayName}`（后端不强约束默认形态，前端 fallback）
 *
 * 设计要点：
 * - 默认形态以 `%` 开头，与后端「metric / group_by.column 不得以 `%` 开头」的限制不冲突
 *   （label 是表头展示名，不参与 SQL 列名生成；列名仍由 `dim_pct_{metric}` 派生）
 * - 用户可自由编辑，支持任意特殊字符（不再限制）
 * - 失焦 / 提交时若 label 为空，会自动回退到本函数返回值
 *   （见 MetricForm.handleLabelBlur / sanitizeDimensionPercentage）
 */
export function defaultLabelForMetric(metricDisplayName: string): string {
  if (!metricDisplayName) return '';
  return `%${metricDisplayName}`;
}

/** 工厂：创建空 metric 项（每次新增生成新引用，避免 selector 误判） */
export function createDefaultDimensionPercentageMetric(): IDimensionPercentageMetricItem {
  return {
    metric: '',
    table_code: 't0',
    label: '',
    group_by: [],
  };
}

/** 工厂：创建空 group_by 项 */
export function createDefaultDimensionPercentageGroupBy(): IDimensionPercentageGroupByItem {
  return {
    table_code: 't0',
    column: '',
  };
}

/** 工厂：创建空 config */
export function createDefaultDimensionPercentageConfig(): IDimensionPercentageConfig {
  return {
    metrics: [],
  };
}
